import i18n from '../i18n';
import { create } from 'zustand';

type Locale = 'es' | 'en';
type Theme = 'light' | 'dark';
type SidebarMode = 'open' | 'mini' | 'hidden';

interface UIState {
  sidebarMode: SidebarMode;
  mobileSidebarOpen: boolean;
  locale: Locale;
  theme: Theme;
  cycleSidebar: () => void;
  setSidebarMode: (mode: SidebarMode) => void;
  setMobileSidebarOpen: (open: boolean) => void;
  toggleMobileSidebar: () => void;
  setLocale: (locale: Locale) => void;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

function getInitialTheme(): Theme {
  const stored = localStorage.getItem('theme');
  if (stored === 'dark' || stored === 'light') return stored;
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

function getInitialSidebarMode(): SidebarMode {
  const stored = localStorage.getItem('sidebar_mode');
  if (stored === 'open' || stored === 'mini' || stored === 'hidden') return stored;
  return 'open';
}

function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  localStorage.setItem('theme', theme);
}

// Apply theme on load
const initialTheme = getInitialTheme();
applyTheme(initialTheme);

const CYCLE_ORDER: SidebarMode[] = ['open', 'mini', 'hidden'];

export const useUIStore = create<UIState>((set) => ({
  sidebarMode: getInitialSidebarMode(),
  mobileSidebarOpen: false,
  locale: (localStorage.getItem('language') as Locale) || 'es',
  theme: initialTheme,

  cycleSidebar: (): void => {
    set((state) => {
      const currentIndex = CYCLE_ORDER.indexOf(state.sidebarMode);
      const nextMode = CYCLE_ORDER[(currentIndex + 1) % CYCLE_ORDER.length];
      localStorage.setItem('sidebar_mode', nextMode);
      return { sidebarMode: nextMode };
    });
  },

  setSidebarMode: (mode: SidebarMode): void => {
    localStorage.setItem('sidebar_mode', mode);
    set({ sidebarMode: mode });
  },

  setMobileSidebarOpen: (open: boolean): void => {
    set({ mobileSidebarOpen: open });
  },

  toggleMobileSidebar: (): void => {
    set((state) => ({ mobileSidebarOpen: !state.mobileSidebarOpen }));
  },

  setLocale: (locale: Locale): void => {
    localStorage.setItem('language', locale);
    i18n.changeLanguage(locale);
    set({ locale });
  },

  toggleTheme: (): void => {
    set((state) => {
      const next: Theme = state.theme === 'light' ? 'dark' : 'light';
      applyTheme(next);
      return { theme: next };
    });
  },

  setTheme: (theme: Theme): void => {
    applyTheme(theme);
    set({ theme });
  },
}));
